import { memo, ReactNode, useState } from 'react';
import { ContentNode } from '@amzn/awscat-pattern-library-service-model';
import {
  TiptapEditorHTMLView,
  Bold,
  Italic,
  Underline,
  Table,
  BulletList,
  Code,
  OrderedList,
  Redo,
  TextAlignCenter,
  TextAlignLeft,
  TextAlignRight,
  Undo,
  Image,
  Link,
  BlockFormatGroup,
  ClearFormatting,
  Youtube,
  CodeBlock,
} from '@amzn/awscat-editor';
import { Box, Button, Container, Header, Modal, SpaceBetween } from '@amzn/awsui-components-core';
import {
  useSectionEditorControlViewModel,
  SectionEditorControlViewModel,
} from './SectionEditorControlViewModel';
import styles from './SectionEditorControl.module.scss';
import { useEntityConverter } from '../v2-hooks/useEntityConverter';
import { TiptapEditorEditingMode, TiptapEditorViewingMode } from './tiptap-editor-integration';

/**
 * TiptapEditor layout configuration for edit mode.
 * `stickyHeader: true` keeps the toolbar pinned at the top of the viewport
 * when the editor content is scrolled, so formatting tools remain accessible.
 */
const EDITOR_EDIT_MODE_CONFIG = { stickyHeader: true };

/**
 * Output formats requested from TiptapEditor on every content change.
 * `json` is the structured Tiptap document (used for persistence and rendering).
 * `html` is the serialized HTML (used for legacy compatibility and validation).
 */
const EDITOR_OUTPUT_FORMATS = ['json', 'html'] as unknown as ('json' | 'html')[];

export interface SectionEditorControlProps {
  /** The content node being edited (section, page, or pattern section). */
  readonly node: ContentNode;
  /** Content ID — for image upload. */
  readonly contentId: string;
  /** Whether the editor is in readonly mode (viewer). Defaults to false. */
  readonly isReadonly?: boolean;
  /** Section title displayed as the collapsible heading. */
  readonly title: string | object;
  /** Description displayed below the heading in muted text. */
  readonly description?: string;
  /** Cloudscape "info" link node rendered inline next to the heading title. */
  readonly info?: ReactNode;
  /** When true, the title heading is not rendered (caller renders it elsewhere). */
  readonly isTitleHidden?: boolean;
  /** Optional buttons rendered next to the heading (e.g. edit/delete section). */
  readonly headerActions?: ReactNode;
  /** When true, focuses the Tiptap editor on mount. Use to direct typing into a freshly added section. */
  readonly isAutoFocused?: boolean;
}

/** Builds the Tiptap menu items. Called once per component instance. */
function buildMenuItems(vm: SectionEditorControlViewModel) {
  return [
    new Undo(),
    new Redo(),
    new BlockFormatGroup(),
    new Bold(),
    new Italic(),
    new Underline(),
    new Code(),
    new CodeBlock(),
    new TextAlignLeft(),
    new TextAlignCenter(),
    new TextAlignRight(),
    new OrderedList(),
    new BulletList(),
    new Link({
      beforeInsert: vm.validateLink,
      // LEGACY: This inline regex validation is duplicated from legacy EditorInput.
      // TiptapEditor uses `validateLink` for real-time keystroke validation (visual feedback)
      // while `beforeInsert` is the async gate before insertion. Both are needed because the
      // editor library treats them as separate concerns. Needs review to unify.
      validateLink: (link) => /(^https:\/\/)|(^www\.)/.test(link),
    }),
    new Image({ imageUpload: vm.uploadImage }),
    new Youtube({ beforeInsert: vm.validateVideo }),
    new Table(),
    new ClearFormatting(),
  ];
}

interface OptionalContainerProps {
  readonly isContainerEnabled: boolean;
  readonly children: ReactNode;
}

/** Renders children inside a Container when enabled, or directly when disabled. */
function OptionalContainer({ isContainerEnabled, children }: OptionalContainerProps) {
  if (isContainerEnabled) {
    return <Container disableContentPaddings={true}>{children}</Container>;
  }
  return <>{children}</>;
}

interface TiptapEditorSelectorProps {
  readonly vm: SectionEditorControlViewModel;
  readonly menuItems: ReturnType<typeof buildMenuItems>;
  readonly isHeaderShown: boolean;
  readonly isAutoFocused: boolean;
}

function TiptapEditorSelector({
  vm,
  menuItems,
  isHeaderShown,
  isAutoFocused,
}: TiptapEditorSelectorProps) {
  const containerClassName = isHeaderShown
    ? `${styles.editorContainer} ${styles.withHeader}`
    : `${styles.editorContainer}`;

  if (!vm.isReadonly) {
    return (
      <div className={containerClassName}>
        <TiptapEditorEditingMode
          config={EDITOR_EDIT_MODE_CONFIG}
          content={vm.initialContent}
          menuItems={menuItems}
          onChange={vm.handleContentChanged}
          outputFormats={EDITOR_OUTPUT_FORMATS}
          entities={vm.entities}
          imageUpload={vm.uploadImage}
          fetchImage={vm.fetchImage}
          editable
          useContainer={false}
          hideFooter={true}
          autoFocus={isAutoFocused}
          inlineTextFilter={vm.inlineTextFilter}
        />
        {vm.validationError && (
          <Modal
            visible
            onDismiss={vm.clearValidationError}
            header="Validation error"
            closeAriaLabel="Close"
            footer={
              <Box float="right">
                <Button variant="primary" onClick={vm.clearValidationError}>
                  OK
                </Button>
              </Box>
            }
          >
            <SpaceBetween size="s">{vm.validationError}</SpaceBetween>
          </Modal>
        )}
      </div>
    );
  }
  // Legacy view to support viewing HTML content.
  if (typeof vm.initialContent === 'string') {
    return (
      <div className={containerClassName}>
        <TiptapEditorHTMLView model={vm.initialContent} />
      </div>
    );
  }

  return (
    <div className={containerClassName}>
      <TiptapEditorViewingMode
        model={vm.initialContent}
        entities={vm.entities}
        fetchImage={vm.fetchImage}
        inlineTextFilter={vm.inlineTextFilter}
      />
    </div>
  );
}

const SectionEditorControl = memo(function SectionEditorControl({
  node,
  contentId,
  isReadonly = false,
  title,
  description,
  info,
  isTitleHidden,
  headerActions,
  isAutoFocused = false,
}: SectionEditorControlProps) {
  const vm = useSectionEditorControlViewModel(node, contentId, isReadonly);

  const [menuItems] = useState(() => buildMenuItems(vm));
  const entityConverter = useEntityConverter();

  const displayTitle = entityConverter(title);
  const isHeaderShown = !isTitleHidden;

  return (
    <OptionalContainer isContainerEnabled={!isReadonly}>
      {isHeaderShown && (
        <div
          className={
            isReadonly
              ? `${styles.headerWrapper} ${styles.headerWrapperReadonly}`
              : styles.headerWrapper
          }
        >
          <Header
            variant={isReadonly ? 'h2' : 'h3'}
            info={info}
            description={
              description && (
                <Box variant="span" color="text-body-secondary" fontSize="body-s">
                  {description}
                </Box>
              )
            }
          >
            {displayTitle}
          </Header>
          {headerActions && <div className={styles.headerActions}>{headerActions}</div>}
        </div>
      )}
      <TiptapEditorSelector
        vm={vm}
        menuItems={menuItems}
        isHeaderShown={isHeaderShown}
        isAutoFocused={isAutoFocused}
      />
    </OptionalContainer>
  );
});

export default SectionEditorControl;
